import React, { useState } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { useCurrentOrg } from 'auth/hooks'
import useProfile from 'champion/hooks/useProfile'
import Loading from 'components/Loading'
import ProfileInfoCard from 'champion/components/ProfileInfoCard'
import { ChampionProfile } from 'champion/interfaces'
import { useBreakPoint } from 'civic-champs-shared/helpers/useBreakpoint'
import { ProfileAccordion } from 'champion/pages/ProfileAccordion'
import { ProfileTabs } from './ProfileTabs'
import PageHeader from 'App/components/PageHeader'
import { useGroup } from '../../group/hooks/useGroup'
import { Group } from '../../Event/components/opportunity/GroupPicker/types'
import { useMembers } from '../../group/hooks/useMembers'
import { isNil } from 'lodash'
import { ContainedButton, OutlinedButton } from '../../civic-champs-shared'
import { useProcessGroupApplication } from '../../group/hooks/useApproveDenyGroupApplication'
import { GroupMembership, MembershipStatusCode } from '../../group/interfaces'
import { useHistory } from 'react-router'
import { useStackBack } from 'core/navigation/useStackBack'

const breakPoint = 1024

const useStyles = makeStyles(theme => ({
  back: {
    fontFamily: 'Nunito, sans-serif',
    fontStyle: 'normal',
    fontWeight: 700,
    fontSize: '14px',
    lineHeight: '20px',
    letterSpacing: '0.1px',
    color: '#0F5DB5',
    textDecoration: 'none',
    display: 'block',
    clear: 'both',
    marginLeft: '-20px',
    marginBottom: '20px',
    '& > img': {
      position: 'relative',
      top: '3px',
      left: '-2px',
    },
  },
  wrapper: {
    display: 'flex',
    width: '100%',
    alignItems: 'start',
    gap: '50px',
    overflowY: 'auto',
    overflowX: 'hidden',
    paddingBottom: '70px', // Additional offset for HubSpot padding
    [theme.breakpoints.down(960)]: {
      flexDirection: 'column',
    },
  },
  sidebarColumn: {
    [theme.breakpoints.up(960)]: {
      flexShrink: 0,
    },
  },
  content: {
    minWidth: 0,
    maxWidth: '100%',
    [theme.breakpoints.up(960)]: {
      flex: '1 1 auto',
      overflowX: 'hidden',
    },
    [theme.breakpoints.down(960)]: {
      width: '100%',
      maxWidth: '427px',
    },
  },
  applicationButtons: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  block: {
    gap: '30px',
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
    maxWidth: '100%',
  },
  deny: {
    color: '#BA1B1B',
    borderColor: '#BA1B1B',
  },
}))

interface ProfileViewProps {
  match: {
    params: {
      id: number
      group?: Group
    }
  }
}

interface ProfilePageProps {
  match: {
    params: {
      id: String
      groupId?: String
    }
  }
}

const getNextApplicantPersonId = (applicants: GroupMembership[], currentPersonId: number) => {
  const currentIndex = applicants.findIndex(member => member.person.id === currentPersonId)
  if (currentIndex === -1) return null

  const remainingApplicants = [...applicants.slice(0, currentIndex), ...applicants.slice(currentIndex + 1)]
  if (remainingApplicants.length === 0) return null

  const nextIndex = currentIndex >= remainingApplicants.length ? remainingApplicants.length - 1 : currentIndex

  return remainingApplicants[nextIndex].person.id
}

export const ProfileViewComponent = ({
  match: {
    params: { id, group },
  },
}: ProfileViewProps) => {
  const history = useHistory()
  const [allMembers, setAllMembers] = useState<GroupMembership[]>([])

  useMembers(group?.id, setAllMembers)

  const processGroupApplication = useProcessGroupApplication()
  const proceedToNextApplication = () => {
    const applicants = allMembers.filter(member => member.status.code === MembershipStatusCode.Applicant)
    const nextPersonId = getNextApplicantPersonId(applicants, id)

    if (nextPersonId) {
      history.push(`/groups/${(group as Group).id}/applications/${nextPersonId}`)
    } else {
      history.push(`/groups/${(group as Group).id}`)
    }
  }
  const approve = async () => {
    await processGroupApplication({
      groupId: (group as Group).id,
      personIds: id,
      approve: true,
    })
    proceedToNextApplication()
  }
  const deny = async () => {
    await processGroupApplication({
      groupId: (group as Group).id,
      personIds: id,
      approve: false,
    })
    proceedToNextApplication()
  }
  const [profile, setProfile] = useState<ChampionProfile | null>(null)
  const currentOrg = useCurrentOrg()
  const { loading, getProfile } = useProfile({
    id,
    onSuccess: setProfile,
    organizationId: currentOrg.id,
    includeTags: true,
    includeGroups: true,
  })
  const classes = useStyles()
  const isSmall = useBreakPoint(breakPoint)
  const isGroupApplicationView = !isNil(group)

  // Check if this person has an approvable application (status === 'Applicant')
  const isApprovableApplication =
    !isNil(group) &&
    allMembers.some(member => member.person.id === id && member.status.code === MembershipStatusCode.Applicant)

  const reloadProfile = React.useCallback(() => {
    getProfile(id)
  }, [getProfile, id])

  if (loading || !profile) {
    return <Loading />
  }

  return (
    <div className={classes.wrapper}>
      <div className={`${classes.block} ${classes.sidebarColumn}`}>
        {isApprovableApplication && isSmall && (
          <div className={classes.applicationButtons}>
            <OutlinedButton onClick={deny} className={classes.deny}>
              Deny
            </OutlinedButton>
            <ContainedButton onClick={approve}>Approve</ContainedButton>
          </div>
        )}
        <ProfileInfoCard {...{ profile, getProfile, isGroupApplicationView }} />
      </div>

      <div className={classes.content}>
        <div className={classes.block}>
          {!isSmall && isApprovableApplication && (
            <div className={classes.applicationButtons}>
              <OutlinedButton onClick={deny} className={classes.deny}>
                Deny
              </OutlinedButton>
              <ContainedButton onClick={approve}>Approve</ContainedButton>
            </div>
          )}
          {isSmall ? (
            <ProfileAccordion {...{ profile, isGroupApplicationView, reloadProfile }} />
          ) : (
            <ProfileTabs {...{ profile, isGroupApplicationView, reloadProfile }} />
          )}
        </div>
      </div>
    </div>
  )
}

export const ProfileView = ({
  match: {
    params: { id, groupId },
  },
}: ProfilePageProps) => {
  const { loading, group } = useGroup(groupId)
  const stackBack = useStackBack({ url: '/champions', label: 'Back To Champions' })

  if (loading && groupId) {
    return <Loading />
  }

  let title: string
  let subTitle: string
  let backLink: string | undefined
  let onBackClick: (() => void) | undefined

  if (group) {
    title = `${group.name} Application`
    subTitle = `Back To ${group.name}`
    backLink = `/groups/${groupId}`
  } else {
    title = 'Profile'
    subTitle = stackBack.label
    onBackClick = stackBack.goBack
  }

  return (
    <PageHeader
      title={title}
      subTitle={subTitle}
      backLink={backLink}
      onBackClick={onBackClick}
      bottomSpaceForHubspotWidget={true}
    >
      <ProfileViewComponent
        match={{
          params: { id: Number(id), group },
        }}
      />
    </PageHeader>
  )
}
